import React from 'react';
import { SystemText } from 'e4shared';
import { Link } from 'react-router-dom';
import Feedback from 'components/asideRight/components/feedback';
import { useDispatch, useSelector } from 'react-redux';
import { setPrismMenu, toggleLeftAside } from 'actions/ui';
import { addMinutes, isPast } from 'helpers/dates';
import { hasModule } from 'helpers/profile';
import Signout from 'components/general/signout';
import BlobImage from 'components/blobImage';
import { posthog } from 'posthog-js';
import { apiGet } from 'services';

export const Menu = () => {
    const dispatch = useDispatch();
    const [showFeedback, setShowFeedback] = React.useState(false);
    const [stats, setStats] = React.useState({});
    const [statsLastLoaded, setStatsLastLoaded] = React.useState(null);
    const { user } = useSelector(state => state.user);
    const active = useSelector(state => state.ui.prismMenu);
    const manager = user.staff > 0;
    const admin = user.administrator;

    const trackEvent = trackingName => {
        dispatch(setPrismMenu(trackingName));
        posthog.capture('left-menu-click', { menuItem: trackingName }, { transport: 'sendBeacon' });
    };

    const loadUserStats = () => {
        apiGet('user/notifications', user.guid, response => {
            setStats(response);
            setStatsLastLoaded(new Date());
        });
    };

    React.useEffect(() => {
        if (!stats || !isPast(addMinutes(statsLastLoaded, 1))) {
            loadUserStats();
        }
    }, []);

    React.useEffect(() => {
        loadUserStats();
    }, [user?.id]);

    return (
        <>
            <aside className="sidebar">
                {/* HOME + COLLAPSE BUTTON */}
                <div className="sidebar__title-row">
                    <span className="sidebar__title">
                        <img src="/dist/img/richardson_sp.png" alt="Richardson" />
                    </span>
                    {/* Desktop collapse button */}
                    <label
                        for="collapse-toggle"
                        className="collapse-btn desktop-collapse"
                        onClick={() => dispatch(toggleLeftAside())}
                    >
                        <i className="fa fa-sidebar"></i>
                    </label>
                    {/* Mobile close button */}
                    <label
                        for="mobile-toggle"
                        className="collapse-btn mobile-close"
                        onClick={() => dispatch(toggleLeftAside())}
                    >
                        <i className="fa fa-times"></i>
                    </label>
                </div>

                {/* NAVIGATION */}
                <ul className="sidebar__nav">
                    <li>
                        <Link
                            className={`sidebar__link ${active === 'Home' ? 'active' : ''}`}
                            to="/"
                            onClick={() => trackEvent('Home')}
                        >
                            <i className="fa fa-home"></i>
                            <span>{SystemText('SideMenu/Home')}</span>
                        </Link>
                    </li>
                    <li>
                        <Link
                            className={`sidebar__link ${active === 'Reviews' ? 'active' : ''}`}
                            to="/develop/reviews"
                            onClick={() => trackEvent('Reviews')}
                        >
                            <i className="fa fa-gauge"></i>
                            <span>{SystemText('SideMenu/Reviews')}</span>
                            {stats.reviews_Count > 0 && <span className="badge">{stats.reviews_Count}</span>}
                        </Link>
                    </li>
                    {hasModule(user, 'pathways') && (
                        <li>
                            <Link
                                className={`sidebar__link ${active === 'Pathways' ? 'active' : ''}`}
                                to="/develop/pathways"
                                onClick={() => trackEvent('Pathways')}
                            >
                                <i className="fa fa-rocket"></i>
                                <span>{SystemText('SideMenu/Pathways')}</span>
                            </Link>
                        </li>
                    )}
                    <li>
                        <Link
                            className={`sidebar__link ${active === 'Learning' ? 'active' : ''}`}
                            to="/develop/learning"
                            onClick={() => trackEvent('Learning')}
                        >
                            <i className="fa fa-book-open"></i>
                            <span>{SystemText('SideMenu/Courses')}</span>
                        </Link>
                    </li>
                    {hasModule(user, 'objectives') && (
                        <li>
                            <Link
                                className={`sidebar__link ${active === 'Objectives' ? 'active' : ''}`}
                                to="/objectives"
                                onClick={() => trackEvent('Objectives')}
                            >
                                <i className="fa fa-circle-check"></i>
                                <span>{SystemText('SideMenu/Objectives')}</span>
                                {stats.objectives_Count > 0 && <span className="badge">{stats.objectives_Count}</span>}
                            </Link>
                        </li>
                    )}
                    {hasModule(user, 'knowledge') && (
                        <li>
                            <Link
                                className={`sidebar__link ${active === 'KnowledgeBank' ? 'active' : ''}`}
                                to="/knowledge-bank"
                                onClick={() => trackEvent('KnowledgeBank')}
                            >
                                <i className="fa fa-comment"></i>
                                <span>{SystemText('SideMenu/Discussions')}</span>
                            </Link>
                        </li>
                    )}
                    {(manager || admin) && (
                        <>
                            <li>
                                <hr />
                            </li>
                            {manager && (
                                <li>
                                    <Link
                                        className={`sidebar__link ${active === 'MyTeam' ? 'active' : ''}`}
                                        //to="/my-team"
                                        to={`/search?manager=${user.id}&name=${user.full_Name}`}
                                        onClick={() => trackEvent('MyTeam')}
                                    >
                                        <i className="fa fa-users"></i>
                                        <span>{SystemText('SideMenu/MyTeam')}</span>
                                        {stats.team_Count > 0 && <span className="badge">{stats.team_Count}</span>}
                                    </Link>
                                </li>
                            )}
                            <li>
                                <Link
                                    className={`sidebar__link ${active === 'Insights' ? 'active' : ''}`}
                                    to="/insights"
                                    onClick={() => trackEvent('Insights')}
                                >
                                    <i className="fa fa-chart-pie"></i>
                                    <span>{SystemText('SideMenu/TeamInsights')}</span>
                                </Link>
                            </li>
                            <li>
                                <hr />
                            </li>
                            <li>
                                <Link
                                    className={`sidebar__link ${active === 'Organisation' ? 'active' : ''}`}
                                    to="/organization"
                                    onClick={() => trackEvent('Organisation')}
                                >
                                    <i className="fa fa-sitemap"></i>
                                    <span>{SystemText('SideMenu/Organisation')}</span>
                                </Link>
                            </li>
                            {admin && (
                                <li>
                                    <Link
                                        className={`sidebar__link ${active === 'Admin' ? 'active' : ''}`}
                                        to="/admin"
                                        onClick={() => trackEvent('Admin')}
                                    >
                                        <i className="fa fa-cog"></i>
                                        <span>{SystemText('SideMenu/Settings')}</span>
                                    </Link>
                                </li>
                            )}
                        </>
                    )}
                    <li className="spacer"></li>
                    <li>
                        <button
                            className={`sidebar__link ${active === 'home' ? 'active' : ''}`}
                            onClick={() => setShowFeedback(true)}
                        >
                            <i className="fa fa-question-circle"></i>
                            <span>{SystemText('SideMenu/Support')}</span>
                        </button>
                    </li>
                </ul>

                <div className="sidebar__spacer"></div>

                {/* USER BLOCK */}
                <div className="sidebar__user">
                    <label for="signout-toggle">
                        <div className="user-block">
                            <div className="avatar">
                                <BlobImage
                                    folder="avatar"
                                    src={user.avatar}
                                    initials={user.initials}
                                    className="img-circle"
                                    width="40"
                                    height="40"
                                />
                            </div>
                            <div className="user-info">
                                <strong>{user.full_Name}</strong>
                                <br />
                                <small style={{ color: 'var(--text-muted)' }}>{user.email}</small>
                            </div>
                            <i className="fa fa-ellipsis-v"></i>
                        </div>
                    </label>
                </div>
                <Feedback externalShow={showFeedback} externalHide={() => setShowFeedback(false)} />
            </aside>
            <ul className="signout-panel">
                <Signout />
            </ul>
        </>
    );
};

export default Menu;
